<template>
  <div
    v-if="shouldShowBanner"
    class="w-full bg-amber-50 text-amber-700 text-sm px-4 py-2 border-l-4 border-amber-500 items-center justify-between fade-in flex"
  >
    <div class="flex-1">
      <span class="flex font-semibold items-center">
        <Flag class="w-4 h-4 mr-1" />
        You've been issued a warning. Reason: {{ mostRecentStrike?.reason_display }}. 
        <router-link to="/my-progress" class="underline font-normal hover:no-underline ml-1">
          Click to learn more
        </router-link>
      </span>
    </div>
    <button
      @click="closeBanner"
      class="ml-4 text-red-500 hover:text-red-700"
      aria-label="Close banner"
    >
      <X class="w-4 h-4" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { Flag, X } from 'lucide-vue-next'
import { useStatsStore } from '@/stores/stats'

const statsStore = useStatsStore()

const isBannerClosed = ref(false)

const mostRecentStrike = computed(() => {
  const strikes = statsStore.stats?.current_user?.strikes
  if (!strikes || strikes.length === 0) return null
  
  return strikes.reduce((latest: any, strike: any) => {
    if (!latest) return strike
    return new Date(strike.created_at) > new Date(latest.created_at) ? strike : latest
  }, null as any)
})

const getStorageKey = (strikeId: number) => `strike_banner_${strikeId}_dismissed`

const shouldShowBanner = computed(() => {
  if (!mostRecentStrike.value || isBannerClosed.value) return false
  
  const strikeDate = new Date(mostRecentStrike.value.created_at)
  const now = new Date()
  const daysSinceStrike = (now.getTime() - strikeDate.getTime()) / (1000 * 60 * 60 * 24)
  
  // Don't show if it's been more than 7 days since the strike was created
  if (daysSinceStrike > 7) return false
  
  // Check if user has manually dismissed this specific strike
  const storageKey = getStorageKey(mostRecentStrike.value.id)
  const dismissedData = localStorage.getItem(storageKey)
  
  if (dismissedData) {
    const { dismissedAt } = JSON.parse(dismissedData)
    const dismissedDate = new Date(dismissedAt)
    const daysSinceDismissed = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24)
    
    // Don't show if it's been more than 7 days since user first saw/dismissed it
    if (daysSinceDismissed > 7) return false
    
    // Don't show if user manually closed it
    return false
  }
  
  return true
})

const closeBanner = () => {
  if (!mostRecentStrike.value) return
  
  // Immediately hide the banner
  isBannerClosed.value = true
  
  // Save to localStorage
  const storageKey = getStorageKey(mostRecentStrike.value.id)
  localStorage.setItem(storageKey, JSON.stringify({
    dismissedAt: new Date().toISOString(),
    strikeId: mostRecentStrike.value.id
  }))
}
</script>